import { useState, useEffect, useCallback } from 'react';

interface Tile {
  tileId: string;
  tileNumber: number;
  snapshotCount: number;
  dateRange: { earliest: string; latest: string } | null;
}

interface Snapshot {
  date: string;
  month: string;
  filename: string;
  url: string;
}

interface SatelliteBrowserProps {
  apiUrl?: string;
}

export function SatelliteBrowser({ apiUrl = `${window.location.protocol}//${window.location.hostname}:3004` }: SatelliteBrowserProps) {
  console.log('SatelliteBrowser mounted with apiUrl:', apiUrl);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [selectedTile, setSelectedTile] = useState<Tile | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [territoryId, setTerritoryId] = useState('arariboia-001');
  const [loading, setLoading] = useState(false);
  const [selectedSnapshots, setSelectedSnapshots] = useState<Snapshot[]>([]);
  const [compareMode, setCompareMode] = useState(false);

  // Fetch tiles with snapshots
  const fetchTiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        territoryId,
        limit: '100',
        ...(searchQuery && { search: searchQuery })
      });
      const url = `${apiUrl}/api/satellite/tiles?${params}`;
      console.log('Fetching tiles from:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('Received tiles:', data.tiles?.length || 0, data);
      setTiles(data.tiles || []);
    } catch (error) {
      console.error('Failed to fetch tiles:', error);
    }
    setLoading(false);
  }, [apiUrl, territoryId, searchQuery]);

  // Fetch snapshots for selected tile
  const fetchSnapshots = useCallback(async (tileId: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/api/satellite/tiles/${tileId}/snapshots?territoryId=${territoryId}`
      );
      const data = await response.json();
      setSnapshots(data.snapshots || []);
    } catch (error) {
      console.error('Failed to fetch snapshots:', error);
    }
    setLoading(false);
  }, [apiUrl, territoryId]);

  useEffect(() => {
    fetchTiles();
  }, [fetchTiles]);

  useEffect(() => {
    if (selectedTile) {
      fetchSnapshots(selectedTile.tileId);
      setSelectedSnapshots([]);
    }
  }, [selectedTile, fetchSnapshots]);

  const handleTileSelect = (tile: Tile) => {
    setSelectedTile(tile);
    setCompareMode(false);
  };

  const handleSnapshotToggle = (snapshot: Snapshot) => {
    if (selectedSnapshots.find(s => s.url === snapshot.url)) {
      setSelectedSnapshots(selectedSnapshots.filter(s => s.url !== snapshot.url));
    } else if (selectedSnapshots.length < 4) {
      setSelectedSnapshots([...selectedSnapshots, snapshot]);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchTiles();
  };

  console.log('Rendering with', tiles.length, 'tiles');

  return (
    <div style={styles.container}>
      {/* Filters */}
      <div style={styles.filters}>
        <form onSubmit={handleSearch} style={styles.searchForm}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Territory</label>
            <select
              value={territoryId}
              onChange={(e) => setTerritoryId(e.target.value)}
              style={styles.select}
            >
              <option value="arariboia-001">Arariboia Indigenous Territory</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Search Tile</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter tile number (e.g., 700000)"
              style={styles.input}
            />
          </div>

          <button type="submit" style={styles.button}>
            Search
          </button>
        </form>
      </div>

      <div style={styles.content}>
        {/* Tiles List */}
        <div style={styles.tilesPanel}>
          <h2 style={styles.panelTitle}>
            Available Tiles ({tiles.length})
          </h2>

          {loading && !selectedTile && (
            <div style={styles.loading}>Loading...</div>
          )}

          <div style={styles.tilesList}>
            {tiles.map((tile) => (
              <div
                key={tile.tileId}
                onClick={() => handleTileSelect(tile)}
                style={{
                  ...styles.tileCard,
                  ...(selectedTile?.tileId === tile.tileId ? styles.tileCardSelected : {})
                }}
              >
                <div style={styles.tileNumber}>Tile #{tile.tileNumber}</div>
                <div style={styles.tileInfo}>
                  {tile.snapshotCount} snapshots
                </div>
                {tile.dateRange && (
                  <div style={styles.tileDates}>
                    {tile.dateRange.earliest} → {tile.dateRange.latest}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Snapshots Grid */}
        <div style={styles.snapshotsPanel}>
          {selectedTile ? (
            <>
              <div style={styles.snapshotsHeader}>
                <h2 style={styles.panelTitle}>
                  Tile #{selectedTile.tileNumber} - {snapshots.length} Snapshots
                </h2>
                {selectedSnapshots.length > 1 && (
                  <button
                    onClick={() => setCompareMode(!compareMode)}
                    style={{
                      ...styles.button,
                      ...(compareMode ? styles.buttonActive : {})
                    }}
                  >
                    {compareMode ? 'Exit Compare' : `Compare (${selectedSnapshots.length})`}
                  </button>
                )}
              </div>

              {compareMode && selectedSnapshots.length > 1 ? (
                <div style={styles.compareGrid}>
                  {selectedSnapshots.map((snapshot) => (
                    <div key={snapshot.url} style={styles.compareItem}>
                      <img
                        src={`${apiUrl}${snapshot.url}`}
                        alt={snapshot.date}
                        style={styles.compareImage}
                      />
                      <div style={styles.compareDate}>{snapshot.date}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <p style={styles.hint}>
                    Click images to select up to 4 for comparison
                  </p>
                  <div style={styles.snapshotsGrid}>
                    {snapshots.map((snapshot) => (
                      <div
                        key={snapshot.url}
                        onClick={() => handleSnapshotToggle(snapshot)}
                        style={{
                          ...styles.snapshotCard,
                          ...(selectedSnapshots.find(s => s.url === snapshot.url)
                            ? styles.snapshotSelected
                            : {})
                        }}
                      >
                        <img
                          src={`${apiUrl}${snapshot.url}`}
                          alt={snapshot.date}
                          style={styles.snapshotImage}
                          loading="lazy"
                        />
                        <div style={styles.snapshotDate}>{snapshot.date}</div>
                        {selectedSnapshots.find(s => s.url === snapshot.url) && (
                          <div style={styles.checkmark}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={styles.placeholder}>
              <p>Select a tile to view snapshots</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  filters: {
    padding: '16px 32px',
    borderBottom: '1px solid #1e293b',
    backgroundColor: '#1e293b'
  },
  searchForm: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase'
  },
  input: {
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #334155',
    borderRadius: '8px',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    width: '240px',
    outline: 'none'
  },
  select: {
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #334155',
    borderRadius: '8px',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    width: '280px',
    outline: 'none',
    cursor: 'pointer'
  },
  button: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#22d3ee',
    color: '#0f172a',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  buttonActive: {
    backgroundColor: '#06b6d4'
  },
  content: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden'
  },
  tilesPanel: {
    width: '300px',
    borderRight: '1px solid #1e293b',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1e293b'
  },
  panelTitle: {
    margin: 0,
    padding: '16px',
    fontSize: '16px',
    fontWeight: 600,
    borderBottom: '1px solid #334155'
  },
  tilesList: {
    flex: 1,
    overflow: 'auto',
    padding: '8px'
  },
  tileCard: {
    padding: '12px',
    marginBottom: '8px',
    borderRadius: '8px',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tileCardSelected: {
    borderColor: '#22d3ee',
    backgroundColor: '#164e63'
  },
  tileNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#22d3ee'
  },
  tileInfo: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px'
  },
  tileDates: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '4px'
  },
  snapshotsPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  snapshotsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px',
    borderBottom: '1px solid #1e293b'
  },
  hint: {
    padding: '0 16px',
    fontSize: '12px',
    color: '#64748b'
  },
  snapshotsGrid: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
    alignContent: 'start'
  },
  snapshotCard: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#1e293b',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  snapshotSelected: {
    borderColor: '#22d3ee',
    boxShadow: '0 0 0 3px rgba(34, 211, 238, 0.3)'
  },
  snapshotImage: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    display: 'block'
  },
  snapshotDate: {
    padding: '8px',
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'center',
    backgroundColor: '#0f172a'
  },
  checkmark: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#22d3ee',
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  compareGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    padding: '16px',
    overflow: 'auto'
  },
  compareItem: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  compareImage: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'contain',
    backgroundColor: '#0f172a'
  },
  compareDate: {
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center'
  },
  placeholder: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b'
  },
  loading: {
    padding: '20px',
    textAlign: 'center',
    color: '#64748b'
  }
};

export default SatelliteBrowser;
